// BaselineAcceptModal.js — PRD-63 WP1
//
// First-scan baseline-accept modal. All four disposition fields are locked
// to specific values (Accept / 12 months / "Initial baseline acceptance" /
// scope=asset) — they're rendered as read-only chips with a lock icon so the
// user understands the audit shape of what they're committing to. The API
// also enforces the lock values; the UI just communicates them.

import React, { useState } from 'react';
import { acceptAllAsBaseline } from '../services/driftService';

const LOCKED_DURATION_MONTHS = 12;

function BaselineAcceptModal({ isOpen, onClose, asset, alarmIds, onSuccess }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    if (!isOpen || !asset || !alarmIds || alarmIds.length === 0) return null;

    const count = alarmIds.length;
    const assetName = asset.assetName || asset.value || asset.target || asset.assetId || 'this asset';
    const currentRiskScore = asset.currentRiskScore ?? null;

    const expiryDate = new Date();
    expiryDate.setMonth(expiryDate.getMonth() + LOCKED_DURATION_MONTHS);
    const expiryDisplay = expiryDate.toLocaleDateString(undefined, {
        year: 'numeric', month: 'long', day: 'numeric'
    });

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await acceptAllAsBaseline(asset.assetId, alarmIds);
            onSuccess && onSuccess({
                bulkBatchId: result.bulkBatchId,
                totalSucceeded: result.totalSucceeded,
                count,
                acceptedAt: new Date().toISOString(),
            });
            onClose();
        } catch (err) {
            // 409 → already baselined (sequential OR two-tab race). Surface
            // the API's structured body so the user sees the winning batch.
            if (err.status === 409) {
                setError(err.body?.error || 'This baseline has already been accepted.');
            } else {
                setError(err.message || 'Failed to accept baseline');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="alarm-modal-backdrop" onClick={loading ? undefined : onClose}>
            <div className="alarm-modal" onClick={e => e.stopPropagation()}>
                <div className="alarm-modal__header">
                    <h3>Accept Initial Baseline for {assetName}</h3>
                    <button className="alarm-modal__close" onClick={onClose} disabled={loading}>×</button>
                </div>
                <div className="alarm-modal__body">
                    <div className="baseline-modal__stats">
                        <div className="baseline-modal__stat">
                            <div className="baseline-modal__stat-label">Open alarms</div>
                            <div className="baseline-modal__stat-value">{count}</div>
                        </div>
                        {currentRiskScore !== null && (
                            <div className="baseline-modal__stat">
                                <div className="baseline-modal__stat-label">Current risk score</div>
                                <div className="baseline-modal__stat-value">{Math.round(currentRiskScore)}</div>
                            </div>
                        )}
                        <div className="baseline-modal__stat">
                            <div className="baseline-modal__stat-label">Expires</div>
                            <div className="baseline-modal__stat-value">{expiryDisplay}</div>
                        </div>
                    </div>

                    <p className="alarm-modal__desc">
                        Accepting {count} alarm{count !== 1 ? 's' : ''} as your initial baseline marks
                        the current state of <strong>{assetName}</strong> as a known set of acceptable
                        risks. Going forward, you'll only be alarmed on NEW findings.
                    </p>

                    <div className="baseline-modal__locked-fields">
                        <div className="baseline-modal__locked-field">
                            <span className="baseline-modal__lock">🔒</span>
                            <span className="baseline-modal__locked-label">Disposition</span>
                            <span className="baseline-modal__locked-value">Accept (acceptable risk — stays in risk score)</span>
                        </div>
                        <div className="baseline-modal__locked-field">
                            <span className="baseline-modal__lock">🔒</span>
                            <span className="baseline-modal__locked-label">Reason</span>
                            <span className="baseline-modal__locked-value">Initial baseline acceptance</span>
                        </div>
                        <div className="baseline-modal__locked-field">
                            <span className="baseline-modal__lock">🔒</span>
                            <span className="baseline-modal__locked-label">Scope</span>
                            <span className="baseline-modal__locked-value">Entire asset ({assetName})</span>
                        </div>
                        <div className="baseline-modal__locked-field">
                            <span className="baseline-modal__lock">🔒</span>
                            <span className="baseline-modal__locked-label">Expires</span>
                            <span className="baseline-modal__locked-value">{LOCKED_DURATION_MONTHS} months from today</span>
                        </div>
                    </div>

                    <div className="baseline-modal__callout">
                        <strong>What happens next.</strong> Your risk score will recompute. From now
                        on you'll be notified only of NEW findings — these {count} stay tracked but
                        won't re-alarm until expiry. Use the <strong>"Accepted (Acceptable Risks)"</strong>
                        {' '}filter any time to review them.
                    </div>

                    {error && <div className="alarm-modal__error">{error}</div>}
                </div>
                <div className="alarm-modal__footer">
                    <button
                        className="alarm-modal__btn alarm-modal__btn--cancel"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        className="alarm-modal__btn alarm-modal__btn--accept"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading
                            ? `Accepting ${count}...`
                            : `Accept ${count} alarm${count !== 1 ? 's' : ''} as baseline`}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default BaselineAcceptModal;
